import { Injectable, NgZone } from '@angular/core';
import API from '@aws-amplify/api-graphql';
import { Hub } from '@aws-amplify/core';
import { Subject } from 'rxjs';
import { RequestService } from '../request/request.service';

export interface CallSignal {
  uuid: string;
  meeting_id: string;
  status: 'ringing' | 'answered' | 'rejected' | 'ended' | 'expired' | 'video_requested';
  has_video: boolean;
  expires_at: number;
  is_recipient: boolean;
}

/** Call-only AppSync subscription: never creates or updates a chat message. */
@Injectable({ providedIn: 'root' })
export class CallSignalingService {
  readonly events = new Subject<CallSignal>();
  private socket?: { unsubscribe(): void };
  private channel?: string;
  private starting?: Promise<void>;
  private retry?: ReturnType<typeof setTimeout>;
  private enabled = false;
  private generation = 0;
  private retryDelay = 1000;
  private failedConnections = 0;
  private readonly maxFailedConnections = 3;

  constructor(private request: RequestService, private zone: NgZone) {
    // Amplify reconnects the socket itself. Reconcile calls that started while
    // offline or while iOS suspended the WebView, after the connection returns.
    Hub.listen('api', ({ payload }) => {
      if (payload.event === 'ConnectionStateChange' && payload.data?.connectionState === 'Connected') {
        if (this.enabled) void this.reconcile('AppSync reconnected').catch(console.warn);
      }
    });
  }

  get deviceToken(): string {
    const key = 'LaniakeaCalls.webDevice';
    let value = localStorage.getItem(key);
    if (!value || !/^[a-f0-9]{64}$/.test(value)) {
      value = Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(key, value);
    }
    return value;
  }

  start(source = 'start'): Promise<void> {
    if (source === 'scheduled retry' || source === 'AppSync reconnected') {
      if (this.failedConnections >= this.maxFailedConnections) return Promise.resolve();
    } else {
      this.failedConnections = 0;
    }
    this.enabled = true;
    if (this.starting) {
      console.log('[Call signaling] Subscribe already in progress', { source });
      return this.starting;
    }
    const generation = this.generation;
    this.starting = this.connect(generation, source).finally(() => {
      if (generation === this.generation) this.starting = undefined;
    });
    return this.starting;
  }

  stop(): void {
    console.log('[Call signaling] Stopped', { hadSocket: !!this.socket, hadRetry: !!this.retry });
    this.enabled = false;
    this.failedConnections = 0;
    this.generation++;
    this.socket?.unsubscribe();
    this.socket = undefined;
    this.channel = undefined;
    this.starting = undefined;
    if (this.retry) clearTimeout(this.retry);
    this.retry = undefined;
  }

  private async connect(generation: number, source: string): Promise<void> {
    try {
      console.log('[Call signaling] iosCall subscribe request', { source, hadSocket: !!this.socket });
      const raw = await this.request.createRequestPost('iosCall', { action: 'subscribe' });
      if (!this.enabled || generation !== this.generation) return;
      const state = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!/^[a-f0-9]{64}$/.test(state?.channel)) throw new Error('Invalid call subscription');
      console.log('[Call signaling] iosCall subscribe response', {
        source, ringingCalls: Array.isArray(state.calls) ? state.calls.length : 0,
        openingSocket: !this.socket || this.channel !== state.channel,
      });
      if (!this.socket || this.channel !== state.channel) {
        this.socket?.unsubscribe();
        this.channel = state.channel;
        const query = `subscription OnCallSignal($channel: String!) {
          onCallSignal(channel: $channel) { uuid meeting_id status has_video expires_at is_recipient }
        }`;
        const observable = API.graphql({ query, variables: { channel: this.channel }, authMode: 'AWS_IAM' }) as any;
        this.socket = observable.subscribe({
          next: (result: any) => {
            if (generation === this.generation && this.enabled) {
              this.failedConnections = 0;
              this.retryDelay = 1000;
              this.deliver(result.value?.data?.onCallSignal);
            }
          },
          error: (error: any) => {
            if (generation !== this.generation || !this.enabled) return;
            const items = Array.isArray(error) ? error :
              (error?.errors || error?.error?.errors || error?.payload?.errors || error?.data?.errors ||
                [error?.error || error]);
            const details = (Array.isArray(items) ? items : [items]).slice(0, 3).map((item: any) => ({
              message: typeof item?.message === 'string' ? item.message : String(item),
              errorType: item?.errorType,
              code: item?.code,
            }));
            const permissionDenied = details.some(item =>
              item.errorType === 'UnauthorizedException' ||
              item.message.includes('UnauthorizedException') ||
              item.message.includes('Permission denied'));
            console.warn('[Call signaling] AppSync subscription failed', {
              errors: details,
              keys: error && typeof error === 'object' ? Object.keys(error).slice(0, 8) : [],
            });
            this.socket?.unsubscribe();
            this.socket = undefined;
            if (permissionDenied) {
              this.failedConnections = this.maxFailedConnections;
              console.warn('[Call signaling] Subscription denied; retries stopped until the next explicit start');
            } else {
              this.scheduleRetry('AppSync subscription failed');
            }
          },
        });
      }
      for (const call of state.calls || []) this.deliver(call);
    } catch (error) {
      console.warn('[Call signaling] iosCall subscribe failed', {
        source, message: error instanceof Error ? error.message : String(error),
      });
      if (generation === this.generation && this.enabled) this.scheduleRetry('subscribe failed');
      throw error;
    }
  }

  async reconcile(source = 'reconcile'): Promise<void> {
    if (this.enabled) await this.start(source);
  }

  private scheduleRetry(reason: string): void {
    if (this.retry || !this.enabled) return;
    if (++this.failedConnections >= this.maxFailedConnections) {
      console.warn('[Call signaling] Retry limit reached', { reason, attempts: this.failedConnections });
      return;
    }
    const delay = this.retryDelay;
    this.retryDelay = Math.min(delay * 2, 30000);
    console.log('[Call signaling] Retry scheduled', { reason, delayMs: delay });
    this.retry = setTimeout(() => {
      this.retry = undefined;
      if (this.enabled) void this.start('scheduled retry').catch(() => undefined);
    }, delay);
  }

  private deliver(call?: CallSignal): void {
    if (!call?.uuid || !call.meeting_id || !['ringing', 'answered', 'rejected', 'ended', 'expired', 'video_requested'].includes(call.status)) return;
    if (call.status === 'ringing' && call.expires_at <= Date.now() / 1000) return;
    this.zone.run(() => this.events.next(call));
  }
}
